import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useAppearance } from "@/components/layout/AppearanceProvider";
import { ErrorState } from "@/components/shared/error-state";
import { ExportButton } from "@/components/shared/export-button";
import { PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { nextSortState, SortableHeader, type SortDir } from "@/components/shared/sortable-header";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { SectionBar } from "@/components/ui/section-bar";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type LossRatioPremiumRow,
  type LossRatioWindow,
  usePolicyLossRatio,
} from "@/hooks/use-policy";
import { lossRatioColor } from "@/lib/appearance";
import { formatCurrency, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

type SortKey =
  | "effectiveDate"
  | "expirationDate"
  | "earnedPremium"
  | "writtenPremium"
  | "incurredLoss"
  | "earnedLossRatio"
  | "writtenLossRatio";

/** Render a loss-ratio percentage. When the denominator is zero (no earned
 *  premium) but losses are non-zero, the API returns 0 — that would render
 *  as a green "0%" and look reassuring even though the policy is bleeding
 *  money. Show "—" instead, with muted styling, so the user can tell the
 *  ratio isn't meaningful. Caller passes both the ratio and the original
 *  numerator/denominator so the no-meaningful-ratio case can be detected. */
function formatPct(value: number | null | undefined): string {
  if (value == null) return "0%";
  return `${value}%`;
}

// A loss ratio is "unreportable" whenever there's no earned premium to
// divide by — regardless of whether incurred losses are zero or positive.
// In both cases the percentage is mathematically undefined, so the cell
// renders as an em-dash rather than a colored "0%" / "X%" that could be
// misread as a real value.
function isMeaninglessRatio(_numerator: number, denominator: number): boolean {
  return denominator === 0;
}

// Loss-ratio cells / values use a user-tunable band scheme from the
// Appearance picker: a color for "below low threshold", another for
// "between thresholds", and another for "above high threshold". Returns
// an inline style the caller spreads onto the rendered cell.
function useLossRatioStyle(): (value: number) => { style: React.CSSProperties } {
  const { appearance } = useAppearance();
  return (value: number) => ({
    style: { color: lossRatioColor(value, appearance.lossRatio) },
  });
}

interface StatRowProps {
  label: string;
  value: React.ReactNode;
  emphasis?: boolean;
}

function StatRow({ label, value, emphasis }: StatRowProps) {
  return (
    <div>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className={cn("mt-0.5 font-semibold text-sm", emphasis && "font-bold text-base")}>
        {value}
      </div>
    </div>
  );
}

// A single loss-ratio panel: SectionBar header + body that shows incurred,
// earned, and the ratio. Each panel sits in its own grid cell so callers
// get fully separate visual chunks instead of two cards huddled under a
// shared header. Subtitle is optional and lives in the SectionBar's
// subtitle slot (the small right-aligned secondary label).
function LossRatioPanel({
  headerTitle,
  headerSubtitle,
  incurredLabel,
  incurred,
  earned,
  lossRatio,
  ratioLabel,
}: {
  headerTitle: string;
  headerSubtitle?: string;
  incurredLabel: string;
  incurred: number;
  earned: number;
  lossRatio: number;
  ratioLabel: string;
}) {
  const ratioMeaningless = isMeaninglessRatio(incurred, earned);
  const ratioStyle = useLossRatioStyle();
  const pct = ratioMeaningless ? null : ratioStyle(lossRatio);
  return (
    <div className="flex h-full flex-col">
      <SectionBar title={headerTitle} subtitle={headerSubtitle} />
      <div className="flex-1 space-y-3 rounded-b-md border border-t-0 bg-card p-3">
        <StatRow label={incurredLabel} value={formatCurrency(incurred)} emphasis />
        <StatRow label="Earned Premium" value={formatCurrency(earned)} />
        <div>
          <div className="text-muted-foreground text-xs">{ratioLabel}</div>
          <div
            className={cn("mt-0.5 font-bold text-lg", ratioMeaningless && "text-muted-foreground")}
            style={ratioMeaningless ? undefined : pct?.style}
            title={ratioMeaningless ? "No earned premium — loss ratio not meaningful" : undefined}
          >
            {ratioMeaningless ? "—" : formatPct(lossRatio)}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to derive the date-range subtitle shown under each calendar /
// accident panel header. Collapses single-year windows ("2026 - 2026") to
// just "2026" so the label stays tight.
function windowRange(data: LossRatioWindow): string {
  return data.startYear === data.endYear
    ? `${data.endYear}`
    : `${data.startYear} - ${data.endYear}`;
}

export function PolicyLossRatioPage() {
  const { policyNumber } = useParams({ strict: false }) as { policyNumber: string };
  const { data, isLoading, isError, refetch } = usePolicyLossRatio(policyNumber);
  const [sortKey, setSortKey] = useState<SortKey>("effectiveDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const ratioStyle = useLossRatioStyle();

  const sortedPremiums = useMemo(() => {
    if (!data?.premiums.length) return [];
    const rows = [...data.premiums];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Date columns come in as ISO strings; lexical compare works for ISO.
      // Numeric columns compare directly.
      let cmp: number;
      if (typeof av === "string" && typeof bv === "string") {
        cmp = av.localeCompare(bv);
      } else if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else if (av == null) {
        cmp = bv == null ? 0 : 1;
      } else if (bv == null) {
        cmp = -1;
      } else {
        cmp = 0;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    // Date and number columns default to descending (newest-first / highest
    // first); the SortableHeader helper takes care of toggling direction
    // when the same key is clicked twice.
    const next = nextSortState({ key: sortKey, dir: sortDir }, key, {
      effectiveDate: "desc",
      expirationDate: "desc",
      earnedPremium: "desc",
      writtenPremium: "desc",
      incurredLoss: "desc",
      earnedLossRatio: "desc",
      writtenLossRatio: "desc",
    });
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 animate-pulse rounded bg-muted" />
        <div className="h-40 animate-pulse rounded-lg border bg-muted/30" />
        <div className="h-72 animate-pulse rounded-lg border bg-muted/30" />
      </div>
    );
  }

  // Network error: surface a retry path. Distinct from "policy has no
  // financial history yet" — that's the (!data) branch below.
  if (isError) {
    return (
      <ErrorState
        message={`Failed to load loss ratio for policy ${policyNumber}.`}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data) {
    return (
      <div className="py-12 text-center">
        <h2 className="font-semibold text-xl">No Loss Ratio Data</h2>
        <p className="mt-2 text-muted-foreground">
          No loss ratio data found for policy {policyNumber}.
        </p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/policy/$policyNumber" params={{ policyNumber }}>
            Back to Policy Dashboard
          </Link>
        </Button>
      </div>
    );
  }

  const pyLabel =
    data.policyYear.startDate && data.policyYear.endDate
      ? `${formatDate(data.policyYear.startDate)} - ${formatDate(data.policyYear.endDate)}`
      : "Policy Year";

  return (
    <div className="space-y-4">
      <PageBreadcrumbs
        trail={[
          {
            key: "policy",
            link: (
              <Link to="/policy/$policyNumber" params={{ policyNumber }}>
                Policy {policyNumber}
              </Link>
            ),
          },
        ]}
        current="Loss Ratio"
      />
      <div className="flex items-center gap-2 rounded-md border bg-card p-3">
        <BackButton label="Back" />
        <div>
          <h1 className="font-semibold text-xl">Policy Loss Ratio · {policyNumber}</h1>
          <p className="text-muted-foreground text-xs">
            Aggregated across all policy terms. The Policy Dashboard shows the selected term in
            isolation.
          </p>
        </div>
      </div>

      {/* Seven individual loss-ratio panels, each with its own header.
          Two-row layout on large screens so panel headers have room to
          breathe and don't truncate:
            Row 1 — 6Y Policy Year | Current Calendar | Current Accident
                    (three across, 4 cols each = 12)
            Row 2 — 3Y Cal | 3Y Acc | 5Y Cal | 5Y Acc
                    (four across, 3 cols each = 12)
          Wraps to two columns on md and a single column below that. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <LossRatioPanel
            headerTitle="6Y Policy Year"
            headerSubtitle={pyLabel}
            incurredLabel="Incurred Loss"
            incurred={data.policyYear.incurredLoss}
            earned={data.policyYear.earnedPremium}
            lossRatio={data.policyYear.lossRatio}
            ratioLabel="Loss Ratio PY"
          />
        </div>
        <div className="lg:col-span-4">
          <LossRatioPanel
            headerTitle="Calendar Year"
            headerSubtitle={windowRange(data.currentYear)}
            incurredLabel="Incurred Loss CY"
            incurred={data.currentYear.calendar.incurredLoss}
            earned={data.currentYear.earnedPremium}
            lossRatio={data.currentYear.calendar.lossRatio}
            ratioLabel="Loss Ratio CY"
          />
        </div>
        <div className="lg:col-span-4">
          <LossRatioPanel
            headerTitle="Accident Year"
            headerSubtitle={windowRange(data.currentYear)}
            incurredLabel="Loss Incurred"
            incurred={data.currentYear.accident.incurredLoss}
            earned={data.currentYear.earnedPremium}
            lossRatio={data.currentYear.accident.lossRatio}
            ratioLabel="Loss Ratio AY"
          />
        </div>
        <div className="lg:col-span-3">
          <LossRatioPanel
            headerTitle="3Y Calendar"
            headerSubtitle={windowRange(data.threeYear)}
            incurredLabel="Incurred Loss CY"
            incurred={data.threeYear.calendar.incurredLoss}
            earned={data.threeYear.earnedPremium}
            lossRatio={data.threeYear.calendar.lossRatio}
            ratioLabel="Loss Ratio CY"
          />
        </div>
        <div className="lg:col-span-3">
          <LossRatioPanel
            headerTitle="3Y Accident"
            headerSubtitle={windowRange(data.threeYear)}
            incurredLabel="Loss Incurred"
            incurred={data.threeYear.accident.incurredLoss}
            earned={data.threeYear.earnedPremium}
            lossRatio={data.threeYear.accident.lossRatio}
            ratioLabel="Loss Ratio AY"
          />
        </div>
        <div className="lg:col-span-3">
          <LossRatioPanel
            headerTitle="5Y Calendar"
            headerSubtitle={windowRange(data.fiveYear)}
            incurredLabel="Incurred Loss CY"
            incurred={data.fiveYear.calendar.incurredLoss}
            earned={data.fiveYear.earnedPremium}
            lossRatio={data.fiveYear.calendar.lossRatio}
            ratioLabel="Loss Ratio CY"
          />
        </div>
        <div className="lg:col-span-3">
          <LossRatioPanel
            headerTitle="5Y Accident"
            headerSubtitle={windowRange(data.fiveYear)}
            incurredLabel="Loss Incurred"
            incurred={data.fiveYear.accident.incurredLoss}
            earned={data.fiveYear.earnedPremium}
            lossRatio={data.fiveYear.accident.lossRatio}
            ratioLabel="Loss Ratio AY"
          />
        </div>
      </div>

      {/* Per-term table */}
      <div>
        <SectionBar
          title="Policy Loss Ratio"
          subtitle={`${data.premiums.length} rows`}
          rightSlot={
            data.premiums.length > 0 ? (
              <ExportButton<LossRatioPremiumRow>
                tone="primary"
                filename={`loss-ratio-${policyNumber}`}
                rows={sortedPremiums}
                columns={[
                  {
                    key: "effectiveDate",
                    label: "Effective Date",
                    value: (r) => r.effectiveDate,
                  },
                  {
                    key: "expirationDate",
                    label: "Expiration Date",
                    value: (r) => r.expirationDate,
                  },
                  {
                    key: "earnedPremium",
                    label: "Earned Premium",
                    value: (r) => r.earnedPremium,
                  },
                  {
                    key: "writtenPremium",
                    label: "Written Premium",
                    value: (r) => r.writtenPremium,
                  },
                  {
                    key: "incurredLoss",
                    label: "Net Incurred Loss",
                    value: (r) => r.incurredLoss,
                  },
                  {
                    key: "earnedLossRatio",
                    label: "Earned Premium LR",
                    value: (r) => r.earnedLossRatio,
                  },
                  {
                    key: "writtenLossRatio",
                    label: "Written Premium LR",
                    value: (r) => r.writtenLossRatio,
                  },
                ]}
              />
            ) : null
          }
        />
        <div className="rounded-b-md border border-t-0 bg-card">
          {data.premiums.length === 0 ? (
            <p className="p-4 text-muted-foreground text-sm">No premium period data available.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Effective Date"
                    sortKey="effectiveDate"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Expiration Date"
                    sortKey="expirationDate"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Earned Premium"
                    sortKey="earnedPremium"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Written Premium"
                    sortKey="writtenPremium"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Net Incurred Loss"
                    sortKey="incurredLoss"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Earned Premium LR"
                    sortKey="earnedLossRatio"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Written Premium LR"
                    sortKey="writtenLossRatio"
                    activeKey={sortKey}
                    activeDir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPremiums.map((p) => {
                  const earnedRatioMeaningless = isMeaninglessRatio(
                    p.incurredLoss,
                    p.earnedPremium,
                  );
                  const writtenRatioMeaningless = isMeaninglessRatio(
                    p.incurredLoss,
                    p.writtenPremium,
                  );
                  const earnedStyle = earnedRatioMeaningless ? null : ratioStyle(p.earnedLossRatio);
                  const writtenStyle = writtenRatioMeaningless
                    ? null
                    : ratioStyle(p.writtenLossRatio);
                  return (
                    <TableRow key={p.effectiveDate}>
                      <TableCell>
                        <Link
                          to="/policy/$policyNumber"
                          params={{ policyNumber }}
                          search={{ term: p.effectiveDate }}
                          className="text-primary hover:underline"
                          title="Open this term on the Policy Dashboard"
                        >
                          {formatDate(p.effectiveDate)}
                        </Link>
                      </TableCell>
                      <TableCell>{p.expirationDate ? formatDate(p.expirationDate) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(p.earnedPremium)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(p.writtenPremium)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(p.incurredLoss)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium tabular-nums",
                          earnedRatioMeaningless && "text-muted-foreground",
                        )}
                        style={earnedRatioMeaningless ? undefined : earnedStyle?.style}
                      >
                        {earnedRatioMeaningless ? "—" : formatPct(p.earnedLossRatio)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium tabular-nums",
                          writtenRatioMeaningless && "text-muted-foreground",
                        )}
                        style={writtenRatioMeaningless ? undefined : writtenStyle?.style}
                      >
                        {writtenRatioMeaningless ? "—" : formatPct(p.writtenLossRatio)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} className="font-semibold">
                    Total
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(data.totalEarned)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(data.totalWritten)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(data.totalIncurred)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right font-medium tabular-nums",
                      isMeaninglessRatio(data.totalIncurred, data.totalEarned) &&
                        "text-muted-foreground",
                    )}
                    style={
                      isMeaninglessRatio(data.totalIncurred, data.totalEarned)
                        ? undefined
                        : ratioStyle(data.earnedLossRatio).style
                    }
                  >
                    {isMeaninglessRatio(data.totalIncurred, data.totalEarned)
                      ? "—"
                      : formatPct(data.earnedLossRatio)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right font-medium tabular-nums",
                      isMeaninglessRatio(data.totalIncurred, data.totalWritten) &&
                        "text-muted-foreground",
                    )}
                    style={
                      isMeaninglessRatio(data.totalIncurred, data.totalWritten)
                        ? undefined
                        : ratioStyle(data.writtenLossRatio).style
                    }
                  >
                    {isMeaninglessRatio(data.totalIncurred, data.totalWritten)
                      ? "—"
                      : formatPct(data.writtenLossRatio)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
